import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    Divider,
    Tooltip,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/features/auth'
import { useLayoutStore } from '@/shared/store/layoutStore'
import { NAV_ITEMS, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './navItems'

const Sidebar = () => {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { user, logout } = useAuthStore()
    const { sidebarCollapsed, toggleSidebar } = useLayoutStore()

    const width = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

    return (
        <Drawer
            variant="permanent"
            sx={{
                width,
                flexShrink: 0,
                transition: 'width 0.2s ease',
                '& .MuiDrawer-paper': {
                    width,
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    transition: 'width 0.2s ease',
                    bgcolor: '#fafafa',
                },
            }}
        >
            {/* Brand */}
            <Box sx={{
                px: 2,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                overflow: 'hidden',
            }}>
                {/* Logo mark */}
                <Box sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 700, lineHeight: 1 }}>
                        €
                    </Typography>
                </Box>
                {!sidebarCollapsed && (
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '13px',
                            letterSpacing: '0.12em',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                        }}
                    >
                        HAUSHALT
                    </Typography>
                )}
            </Box>

            <Divider />

            {/* Nav items */}
            <List sx={{ flexGrow: 1, px: 1, pt: 1 }}>
                {NAV_ITEMS.map((item) => {
                    const Icon = item.icon
                    const isActive = pathname === item.path
                    const label = t(item.labelKey)

                    const btn = (
                        <ListItemButton
                            onClick={() => navigate(item.path)}
                            selected={isActive}
                            sx={{
                                minHeight: 40,
                                borderRadius: '6px',
                                justifyContent: sidebarCollapsed ? 'center' : 'initial',
                                mb: 0.5,
                                pl: sidebarCollapsed ? 1.5 : 1.5,
                                borderLeft: isActive ? '2px solid' : '2px solid transparent',
                                borderColor: isActive ? 'primary.main' : 'transparent',
                                '&.Mui-selected': {
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                                    color: 'primary.main',
                                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                                    '&:hover': {
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                    },
                                },
                                '&:not(.Mui-selected):hover': {
                                    bgcolor: 'rgba(0,0,0,0.04)',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: sidebarCollapsed ? 0 : 1.5,
                                    justifyContent: 'center',
                                    color: isActive ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                <Icon size={16} strokeWidth={isActive ? 2.5 : 1.8} />
                            </ListItemIcon>
                            {!sidebarCollapsed && (
                                <ListItemText
                                    primary={label}
                                    primaryTypographyProps={{ fontSize: '13px', fontWeight: isActive ? 600 : 400 }}
                                />
                            )}
                        </ListItemButton>
                    )

                    return sidebarCollapsed ? (
                        <Tooltip key={item.path} title={label} placement="right">
                            <span>{btn}</span>
                        </Tooltip>
                    ) : (
                        <span key={item.path}>{btn}</span>
                    )
                })}
            </List>

            <Divider />

            {/* User + collapse toggle */}
            <Box sx={{ p: 1 }}>
                <Tooltip
                    title={sidebarCollapsed ? `${user?.name || 'User'} — Abmelden` : ''}
                    placement="right"
                >
                    <ListItemButton
                        onClick={logout}
                        sx={{
                            borderRadius: '6px',
                            minHeight: 40,
                            justifyContent: sidebarCollapsed ? 'center' : 'initial',
                            mb: 0.5,
                            '&:hover': { bgcolor: 'rgba(220,38,38,0.06)', color: 'error.main', '& .logout-icon': { color: 'error.main' } },
                        }}
                    >
                        <ListItemIcon
                            className="logout-icon"
                            sx={{ minWidth: 0, mr: sidebarCollapsed ? 0 : 1.5, justifyContent: 'center', color: 'text.secondary' }}
                        >
                            <LogOut size={16} strokeWidth={1.8} />
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                            <ListItemText
                                primary={user?.name || user?.email || 'User'}
                                secondary="Abmelden"
                                primaryTypographyProps={{ fontSize: '13px', fontWeight: 500, noWrap: true }}
                                secondaryTypographyProps={{ fontSize: '11px' }}
                            />
                        )}
                    </ListItemButton>
                </Tooltip>

                <Tooltip title={sidebarCollapsed ? 'Aufklappen' : ''} placement="right">
                    <ListItemButton
                        onClick={toggleSidebar}
                        sx={{
                            borderRadius: '6px',
                            minHeight: 32,
                            justifyContent: sidebarCollapsed ? 'center' : 'initial',
                        }}
                    >
                        <ListItemIcon
                            sx={{ minWidth: 0, mr: sidebarCollapsed ? 0 : 1.5, justifyContent: 'center', color: 'text.secondary' }}
                        >
                            {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                            <ListItemText
                                primary="Einklappen"
                                primaryTypographyProps={{ fontSize: '11px', color: 'text.secondary' }}
                            />
                        )}
                    </ListItemButton>
                </Tooltip>
            </Box>
        </Drawer>
    )
}

export default Sidebar
